import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import './ProdutoList.css';
import './LaudoList.css';

const NOTIFICAR_OPTS = [
  { value: 'ambos',   label: 'Cliente + Dono' },
  { value: 'cliente', label: 'Somente Cliente' },
  { value: 'dono',    label: 'Somente Dono' },
];

const EMPTY_FORM = {
  cliente: '', titulo: '', descricao: '',
  arquivo: null, data_emissao: '', data_vencimento: '', notificar: 'ambos',
};

function urgencyInfo(dias) {
  if (dias < 0)  return { cls: 'laudo-badge--vencido',  label: 'Vencido',        icon: 'event_busy' };
  if (dias === 0) return { cls: 'laudo-badge--hoje',    label: 'Vence hoje',      icon: 'warning' };
  if (dias <= 7)  return { cls: 'laudo-badge--critico', label: `${dias}d`,        icon: 'warning' };
  if (dias <= 30) return { cls: 'laudo-badge--alerta',  label: `${dias}d`,        icon: 'schedule' };
  return          { cls: 'laudo-badge--ok',             label: `${dias}d`,        icon: 'check_circle' };
}

function fmtDate(s) {
  if (!s) return '—';
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y}`;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function LaudoList() {
  const [laudos, setLaudos]     = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filtro, setFiltro]     = useState('todos');

  const [modal, setModal]       = useState(false);
  const [editId, setEditId]     = useState(null);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetchLaudos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/laudos/');
      setLaudos(res.data);
    } catch {
      toast.error('Erro ao carregar laudos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLaudos();
    api.get('/clientes/').then(r => setClientes(r.data)).catch(() => {});
  }, [fetchLaudos]);

  const openNovo = () => {
    setEditId(null);
    setForm({ ...EMPTY_FORM, data_emissao: today() });
    setModal(true);
  };

  const openEdit = (l) => {
    setEditId(l.id);
    setForm({
      cliente: l.cliente, titulo: l.titulo, descricao: l.descricao || '',
      arquivo: null, data_emissao: l.data_emissao, data_vencimento: l.data_vencimento,
      notificar: l.notificar,
    });
    setModal(true);
  };

  const closeModal = () => { setModal(false); setEditId(null); setForm(EMPTY_FORM); };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setForm(prev => ({ ...prev, [name]: files ? files[0] : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.cliente || !form.titulo || !form.data_emissao || !form.data_vencimento) {
      toast.error('Preencha todos os campos obrigatórios.');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v !== null && v !== '') fd.append(k, v);
      });
      const cfg = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (editId) {
        await api.patch(`/laudos/${editId}/`, fd, cfg);
        toast.success('Laudo atualizado.');
      } else {
        await api.post('/laudos/', fd, cfg);
        toast.success('Laudo criado.');
      }
      closeModal();
      fetchLaudos();
    } catch (err) {
      if (!err._planLimitHandled)
        toast.error('Erro ao salvar laudo.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este laudo?')) return;
    setDeleting(id);
    try {
      await api.delete(`/laudos/${id}/`);
      toast.success('Laudo excluído.');
      setLaudos(prev => prev.filter(l => l.id !== id));
    } catch {
      toast.error('Erro ao excluir laudo.');
    } finally {
      setDeleting(null);
    }
  };

  const filtered = laudos.filter(l => {
    const q = search.toLowerCase();
    const match = l.titulo.toLowerCase().includes(q) ||
      (l.cliente_nome || '').toLowerCase().includes(q);
    if (!match) return false;
    if (filtro === 'critico') return l.dias_para_vencer >= 0 && l.dias_para_vencer <= 7;
    if (filtro === 'alerta')  return l.dias_para_vencer > 7  && l.dias_para_vencer <= 30;
    if (filtro === 'vencido') return l.dias_para_vencer < 0;
    return true;
  });

  const counts = {
    critico: laudos.filter(l => l.dias_para_vencer >= 0 && l.dias_para_vencer <= 7).length,
    alerta:  laudos.filter(l => l.dias_para_vencer > 7 && l.dias_para_vencer <= 30).length,
    vencido: laudos.filter(l => l.dias_para_vencer < 0).length,
  };

  return (
    <div className="produto-page">

      {/* ── Header ──────────────────────────────────────── */}
      <div className="produto-page-header">
        <div className="produto-page-title-row">
          <h1 className="produto-page-title">Laudos</h1>
          <div className="produto-tabs">
            {[
              { key: 'todos',   label: 'Todos' },
              { key: 'critico', label: `Crítico${counts.critico ? ` (${counts.critico})` : ''}` },
              { key: 'alerta',  label: `Alerta${counts.alerta  ? ` (${counts.alerta})` : ''}` },
              { key: 'vencido', label: `Vencido${counts.vencido ? ` (${counts.vencido})` : ''}` },
            ].map(t => (
              <span
                key={t.key}
                className={`produto-tab${filtro === t.key ? ' active' : ''}`}
                onClick={() => setFiltro(t.key)}
              >{t.label}</span>
            ))}
          </div>
        </div>

        <div className="produto-header-actions">
          <div className="produto-search-wrap">
            <span className="material-symbols-outlined">search</span>
            <input
              placeholder="Buscar por título ou cliente…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button className="prd-btn-primary" onClick={openNovo}>
            <span className="material-symbols-outlined">add</span>
            Novo Laudo
          </button>
          <button className="prd-icon-btn" onClick={fetchLaudos} title="Atualizar">
            <span className="material-symbols-outlined">refresh</span>
          </button>
        </div>
      </div>

      {/* ── Tabela ──────────────────────────────────────── */}
      <div className="prd-table-card">
        <div className="prd-table-wrap">
          <table className="prd-table">
            <thead>
              <tr>
                <th className="left">Título</th>
                <th className="left">Cliente</th>
                <th className="center">Emissão</th>
                <th className="center">Vencimento</th>
                <th className="center">Status</th>
                <th className="center">Notificar</th>
                <th className="center">Alertas</th>
                <th className="center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="center" style={{ padding: 32, color: '#74777d' }}>Carregando…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="center" style={{ padding: 32, color: '#74777d' }}>Nenhum laudo encontrado.</td></tr>
              ) : filtered.map(l => {
                const urg = urgencyInfo(l.dias_para_vencer);
                return (
                  <tr key={l.id} className={`laudo-row laudo-row--${urg.cls.replace('laudo-badge--', '')}`}>
                    <td className="left td-name">{l.titulo}</td>
                    <td className="left">{l.cliente_nome}</td>
                    <td className="center">{fmtDate(l.data_emissao)}</td>
                    <td className="center">{fmtDate(l.data_vencimento)}</td>
                    <td className="center">
                      <span className={`laudo-badge ${urg.cls}`}>
                        <span className="material-symbols-outlined">{urg.icon}</span>
                        {urg.label}
                      </span>
                    </td>
                    <td className="center">
                      <span className="laudo-notif-label">
                        {NOTIFICAR_OPTS.find(o => o.value === l.notificar)?.label || l.notificar}
                      </span>
                    </td>
                    <td className="center">
                      <div className="laudo-notif-icons">
                        <span
                          className={`material-symbols-outlined laudo-notif-icon ${l.notificado_30d_em ? 'sent' : 'pending'}`}
                          title={l.notificado_30d_em ? `30d enviado em ${new Date(l.notificado_30d_em).toLocaleDateString('pt-BR')}` : '30d pendente'}
                        >notifications</span>
                        <span
                          className={`material-symbols-outlined laudo-notif-icon ${l.notificado_7d_em ? 'sent' : 'pending'}`}
                          title={l.notificado_7d_em ? `7d enviado em ${new Date(l.notificado_7d_em).toLocaleDateString('pt-BR')}` : '7d pendente'}
                        >notifications_active</span>
                      </div>
                    </td>
                    <td className="center">
                      <div className="td-actions" style={{ justifyContent: 'center' }}>
                        {l.arquivo && (
                          <a
                            href={l.arquivo}
                            target="_blank"
                            rel="noreferrer"
                            className="prd-icon-btn"
                            title="Baixar arquivo"
                          >
                            <span className="material-symbols-outlined">download</span>
                          </a>
                        )}
                        <button
                          className="prd-icon-btn"
                          onClick={() => openEdit(l)}
                          title="Editar"
                        >
                          <span className="material-symbols-outlined">edit</span>
                        </button>
                        <button
                          className="prd-icon-btn prd-icon-btn--danger"
                          onClick={() => handleDelete(l.id)}
                          disabled={deleting === l.id}
                          title="Excluir"
                        >
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modal novo/editar ────────────────────────────── */}
      {modal && (
        <div className="laudo-modal-overlay" onClick={closeModal}>
          <div className="laudo-modal" onClick={e => e.stopPropagation()}>
            <div className="laudo-modal-header">
              <h2>{editId ? 'Editar Laudo' : 'Novo Laudo'}</h2>
              <button className="prd-icon-btn" onClick={closeModal}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="laudo-form">
              <div className="laudo-form-row">
                <label>Cliente *</label>
                <select name="cliente" value={form.cliente} onChange={handleChange} required>
                  <option value="">Selecione o cliente…</option>
                  {clientes.map(c => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>

              <div className="laudo-form-row">
                <label>Título *</label>
                <input
                  name="titulo"
                  value={form.titulo}
                  onChange={handleChange}
                  placeholder="Ex: Laudo de Inspeção Anual"
                  required
                />
              </div>

              <div className="laudo-form-row">
                <label>Descrição</label>
                <textarea
                  name="descricao"
                  value={form.descricao}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Observações sobre o laudo…"
                />
              </div>

              <div className="laudo-form-grid">
                <div className="laudo-form-row">
                  <label>Data de emissão *</label>
                  <input type="date" name="data_emissao" value={form.data_emissao} onChange={handleChange} required />
                </div>
                <div className="laudo-form-row">
                  <label>Data de vencimento *</label>
                  <input type="date" name="data_vencimento" value={form.data_vencimento} onChange={handleChange} required />
                </div>
              </div>

              <div className="laudo-form-row">
                <label>Notificar</label>
                <select name="notificar" value={form.notificar} onChange={handleChange}>
                  {NOTIFICAR_OPTS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div className="laudo-form-row">
                <label>Arquivo do laudo (PDF / imagem)</label>
                <input type="file" name="arquivo" accept=".pdf,.png,.jpg,.jpeg" onChange={handleChange} />
                {editId && <p className="laudo-form-hint">Deixe em branco para manter o arquivo atual.</p>}
              </div>

              <div className="laudo-form-actions">
                <button type="button" className="prd-btn-ghost" onClick={closeModal} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="prd-btn-primary" disabled={saving}>
                  {saving ? 'Salvando…' : (editId ? 'Salvar alterações' : 'Criar laudo')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
